import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPendingDuesReport } from "../../../state/reducers/pendingAmountsSlice";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

const PendingDuesChart = () => {
  const dispatch = useDispatch();
  const { monthly, yearly, status, error } = useSelector(
    (state) => state.pendingDuesReport
  );
  const [groupBy, setGroupBy] = useState("month");
  const [reportType, setReportType] = useState("Analytics");
  const [generateClicked, setGenerateClicked] = useState(false);

  useEffect(() => {
    dispatch(fetchPendingDuesReport(groupBy));
  }, [dispatch, groupBy]);

  const handleGenerate = () => {
    setGenerateClicked(true);
  };

  const getChartData = () => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
  
    if (status === "succeeded") {
      if (groupBy === "month" && monthly) {
        const pendingAmounts = new Array(12).fill(0); // Initialize with zeros for all months
  
        // Populate pending amounts for available months
        monthly.forEach((yearData) => {
          yearData.monthly_pending.forEach((data) => {
            const monthIndex = months.indexOf(data.month);
            if (monthIndex !== -1) {
              pendingAmounts[monthIndex] += data.total_pending;
            }
          });
        });
  
        return {
          labels: months,
          datasets: [
            {
              label: `Monthly Pending Dues`,
              data: pendingAmounts,
              backgroundColor: "#1e40af",
              borderColor: "#00008B",
              borderWidth: 1,
            },
          ],
        };
      }
  
      if (groupBy === "year" && yearly) {
        const years = yearly.map((data) => data.year);
        const pendingAmounts = yearly.map((data) => data.total_pending);
  
        return {
          labels: years,
          datasets: [
            {
              label: `Yearly Pending Dues`,
              data: pendingAmounts,
              backgroundColor: "#1e40af",
              borderColor: "#00008B",
              borderWidth: 1,
            },
          ],
        };
      }
    }
  
    // Return default structure if no data is available
    return {
      labels: groupBy === "month" ? months : [],
      datasets: [
        {
          label: "No Data Available",
          data: [],
          backgroundColor: "#f59e0b",
          borderColor: "#c2410c",
          borderWidth: 1,
        },
      ],
    };
  };
  

  const renderList = () => {
    if (generateClicked && status === "succeeded") {
      let pendingData = [];

      if (groupBy === "month" && monthly) {
        pendingData = monthly.flatMap((yearData) =>
          yearData.monthly_pending.map((data) => ({
            year: yearData.year,
            month: data.month,
            total_pending: data.total_pending,
          }))
        );
      } else if (groupBy === "year" && yearly) {
        pendingData = yearly.map((data) => ({
          year: data.year,
          total_pending: data.total_pending,
        }));
      }

      if (pendingData.length === 0) {
        return <div className="text-center text-gray-500">No data available</div>;
      }

      return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold mb-4">Pending Dues Data</h3>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Year</th>
                <th className="px-4 py-2 text-left">
                  {groupBy === "month" ? "Month" : "Total Pending"}
                </th>
                <th className="px-4 py-2 text-left">Total Pending</th>
              </tr>
            </thead>
            <tbody>
              {pendingData.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-2">{item.year}</td>
                  <td className="px-4 py-2">{item.month || item.total_pending}</td>
                  <td className="px-4 py-2">{item.total_pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="text-center text-gray-500">No data available</div>;
  };

  const chartData = getChartData();

  return (
    <div className="bg-white p-6 rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Pending Dues Report</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setGroupBy("month")}
            className={`px-4 py-2 rounded-md ${
              groupBy === "month" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setGroupBy("year")}
            className={`px-4 py-2 rounded-md ${
              groupBy === "year" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <select
          className="px-4 py-2 rounded-md"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
        >
          <option value="Analytics">Analytics</option>
          <option value="List">List</option>
        </select>
        <button
          onClick={handleGenerate}
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Generate
        </button>
      </div>

      {status === "loading" && <p className="text-gray-500">Loading...</p>}
      {status === "failed" && <p className="text-red-500">{error}</p>}

      {reportType === "Analytics" ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: true },
            },
            scales: {
              y: {
                beginAtZero: true,
              },
            },
          }}
        />
      ) : (
        renderList()
      )}
    </div>
  );
};

export default PendingDuesChart;
